import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { APP_CONFIG } from '@application/config';
import { withoutForbiddenElevation } from '@application/data-access';
import type { Guid } from '../entities/academy-enums.model';
import {
  ChatViewerItem,
  MutedParticipantItem,
  ReactionCounts,
} from '../entities/academy-responses.model';

/**
 * HTTP client for the live chat custom endpoints (`/LiveEvents/{id}/chat/*` and
 * `/reactions`). Source of truth: `docs/academy-api-integration.md` §7.6. PascalCase.
 * Live message SENDING/reactions are the SignalR hub's job — this client only does REST
 * reads (history, reactions, muted list) and moderation (remove / mute / unmute).
 */
@Injectable({ providedIn: 'root' })
export class LiveChatService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = inject(APP_CONFIG).apiUrl.academy_service;

  /**
   * `GET /LiveEvents/{id}/chat/history?top={take}` — the chat as a viewer reads it (backend
   * 014): newest first, with `PersonName` and `IsProducer`, removed messages left out. On a
   * single-room live it holds every account's messages; on a live from before 014 the server
   * returns only the caller's account, so the one read serves both. `take` defaults to 100
   * (above 200 answers 422).
   * ⚠ 403 outside the event's audience · 404 when the live is not visible to the caller:
   * marked `withoutForbiddenElevation()` so the watch page does not open the
   * temporary-elevation modal over a read it did alone.
   */
  public history(id: Guid, take = 100): Observable<ChatViewerItem[]> {
    return this.http.get<ChatViewerItem[]>(
      `${this.baseUrl}/LiveEvents/${encodeURIComponent(id)}/chat/history`,
      {
        params: new HttpParams().set('top', String(take)),
        context: withoutForbiddenElevation(),
        withCredentials: true,
      },
    );
  }

  /**
   * `GET /LiveEvents/{id}/chat/muted` — who is muted in the live's chat, every account
   * (backend round 24). Producer only: ⚠ 403 not producer · 404 not found. A read the studio
   * does alone when it opens, hence `withoutForbiddenElevation()`.
   */
  public muted(id: Guid): Observable<MutedParticipantItem[]> {
    return this.http.get<MutedParticipantItem[]>(
      `${this.baseUrl}/LiveEvents/${encodeURIComponent(id)}/chat/muted`,
      { context: withoutForbiddenElevation(), withCredentials: true },
    );
  }

  /**
   * `GET /LiveEvents/{id}/reactions` — aggregated reaction counts keyed by name.
   * ⚠ 403 outside the event's audience: marked `withoutForbiddenElevation()` so the
   * watch page does not open the temporary-elevation modal over a read it did alone.
   */
  public reactions(id: Guid): Observable<ReactionCounts> {
    return this.http.get<ReactionCounts>(
      `${this.baseUrl}/LiveEvents/${encodeURIComponent(id)}/reactions`,
      { context: withoutForbiddenElevation(), withCredentials: true },
    );
  }

  /**
   * `POST /LiveEvents/{id}/chat/messages/{messageId}/remove` — moderate (hide) a message.
   * Sends an empty body.
   */
  public removeMessage(id: Guid, messageId: Guid): Observable<void> {
    return this.http.post<void>(
      `${this.baseUrl}/LiveEvents/${encodeURIComponent(id)}/chat/messages/${encodeURIComponent(
        messageId,
      )}/remove`,
      {},
      { withCredentials: true },
    );
  }

  /**
   * `POST /LiveEvents/{id}/chat/participants/{personId}/mute` — mute a participant.
   * Sends an empty body.
   */
  public mute(id: Guid, personId: Guid): Observable<void> {
    return this.http.post<void>(
      `${this.baseUrl}/LiveEvents/${encodeURIComponent(id)}/chat/participants/${encodeURIComponent(
        personId,
      )}/mute`,
      {},
      { withCredentials: true },
    );
  }

  /**
   * `POST /LiveEvents/{id}/chat/participants/{personId}/unmute` — unmute a participant.
   * Sends an empty body.
   */
  public unmute(id: Guid, personId: Guid): Observable<void> {
    return this.http.post<void>(
      `${this.baseUrl}/LiveEvents/${encodeURIComponent(id)}/chat/participants/${encodeURIComponent(
        personId,
      )}/unmute`,
      {},
      { withCredentials: true },
    );
  }
}
